// KnowledgeAdmin — standalone admin panel for the AI Chat self-evolving
// knowledge base. Same family as pages/AdminPage.jsx (hero + tab shell).
// Lists every lesson regardless of status (approved / pending / rejected).
//
// Access model (locked 30 Jul 2026, see ai-chat-tracker.md Part 5):
//   - Any `amazon-internal` user can VIEW this page and ADD a new lesson
//     (their addition lands as "pending" until an admin approves it).
//   - Only `admins` can Edit/Delete an existing lesson, and an admin's
//     own additions are auto-approved immediately.
// The route itself is gated `internalOnly` in App.jsx; this component
// additionally hides/disables the write actions for non-admins so the UI
// matches what the backend will actually allow (server-side enforcement
// is the real gate — see admin-api/routes/knowledge.py).

import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  SpaceBetween, Container, Header, Box, Button, Table, StatusIndicator,
  Spinner, Alert, Select, Textarea, Modal, FormField, Tabs, TextFilter, Input,
} from "@cloudscape-design/components";
import ThemeToggle from "../image-studio/ThemeToggle.jsx";
import { useAuth } from "../../auth/AuthProvider.jsx";
import { aiChatApi } from "./aiChatApi.js";
import SchemaAdminTab from "./SchemaAdminTab.jsx";
import QueriesAdminTab from "./QueriesAdminTab.jsx";
import "./ai-chat.css";

const STATUS_FILTER_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Approved", value: "approved" },
  { label: "Pending", value: "pending" },
  { label: "Rejected", value: "rejected" },
];

const CATEGORY_OPTIONS = [
  { label: "Metric definition", value: "metric_definition" },
  { label: "Correction", value: "correction" },
  { label: "Gotcha", value: "gotcha" },
  { label: "Preference", value: "preference" },
];

const STATUS_INDICATOR_TYPE = { approved: "success", pending: "in-progress", rejected: "error" };

// Source column — traces whether a lesson came from the agent (extracted
// from a real conversation, then confirmed in-chat) or was typed directly
// into this admin page. `proposed_by_session` is the field that carries
// this: agent-proposed rows hold a real `sess_...` id, manual rows hold
// the sentinel `manual:<username>` (see knowledge.py::create_manual).
//
// Semantics locked 30 Jul 2026 (Part 10): admin is just the APPROVER —
// a separate gatekeeping role, not the decision-maker. The person who
// "sourced" a lesson is whoever chose to save it in the first place,
// via chat or manually, admin or not. For agent-proposed rows that's
// `confirmed_by` if present (a non-admin's in-chat "Save this" click,
// which lands the row `pending` — see Part 9), falling back to
// `decided_by` only when `confirmed_by` was never set — i.e. an ADMIN
// clicked "Save this" directly in chat, which both decides AND approves
// in the same action, so only `decided_by` exists for that row. Once a
// non-admin's `confirmed_by` is set, it's never overwritten by a later
// admin approval (`approve()` only ever touches `decided_by`), so this
// column keeps showing the real submitter even after the row is
// approved — the "Approved by" column (below) is where the approver's
// name lives instead.
function formatSource(item) {
  const session = item.proposed_by_session || "";
  if (session.startsWith("manual:")) {
    const username = session.slice("manual:".length);
    return { icon: "✍️", label: "Explicit", username };
  }
  return { icon: "🤖", label: "Agent", username: item.confirmed_by || item.decided_by || null };
}

// KnowledgeAdmin is now a 3-tab shell (added 3 Aug 2026 for the
// retrieval-architecture rebuild — see ai-chat-tracker.md "Schema
// metadata / sample queries / knowledge" section): Lessons (this file's
// original content, unchanged), Table Schema (SchemaAdminTab.jsx), and
// Sample Queries (QueriesAdminTab.jsx). All three share the same
// amazon-internal-view / admins-write access model and the same page
// shell (hero + studio-content wrapper) — only the body below the hero
// switches per tab.
function LessonsTab() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [decidingId, setDecidingId] = useState(null); // approve/reject in flight
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  const filteredItems = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      if (item.content?.toLowerCase().includes(q)) return true;
      if (item.category?.toLowerCase().includes(q)) return true;
      if ((item.applies_to || []).some((t) => t.toLowerCase().includes(q))) return true;
      if (item.decided_by?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [items, searchText]);

  const load = async (status) => {
    setLoading(true);
    setError("");
    try {
      const data = await aiChatApi.listKnowledge(status || undefined);
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || "Failed to load knowledge base.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(statusFilter.value); }, [statusFilter]);

  const handleDelete = async (knowledgeId) => {
    const confirmed = window.confirm(
      "Delete this learned fact? Future answers will no longer use it. This cannot be undone."
    );
    if (!confirmed) return;
    setDeletingId(knowledgeId);
    try {
      await aiChatApi.deleteKnowledge(knowledgeId);
      setItems((prev) => prev.filter((i) => i.knowledge_id !== knowledgeId));
    } catch (err) {
      setError(err.message || "Failed to delete.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleApprove = async (knowledgeId) => {
    setDecidingId(knowledgeId);
    try {
      const updated = await aiChatApi.approveKnowledge(knowledgeId);
      setItems((prev) => prev.map((i) => (i.knowledge_id === knowledgeId ? updated : i)));
    } catch (err) {
      setError(err.message || "Failed to approve.");
    } finally {
      setDecidingId(null);
    }
  };

  const handleReject = async (knowledgeId) => {
    const confirmed = window.confirm("Reject this proposed fact? It will not be used by the agent.");
    if (!confirmed) return;
    setDecidingId(knowledgeId);
    try {
      const updated = await aiChatApi.rejectKnowledge(knowledgeId);
      setItems((prev) => prev.map((i) => (i.knowledge_id === knowledgeId ? updated : i)));
    } catch (err) {
      setError(err.message || "Failed to reject.");
    } finally {
      setDecidingId(null);
    }
  };

  const handleSaveEdit = async ({ content, category, applies_to }) => {
    try {
      await aiChatApi.updateKnowledge(editingItem.knowledge_id, { content, category, applies_to });
      setItems((prev) =>
        prev.map((i) => (i.knowledge_id === editingItem.knowledge_id ? { ...i, content, category, applies_to } : i))
      );
      setEditingItem(null);
    } catch (err) {
      setError(err.message || "Failed to save.");
    }
  };

  const handleAdd = async ({ content, category, applies_to }) => {
    const created = await aiChatApi.createKnowledge({ content, category, applies_to: applies_to || [] });
    setItems((prev) => [created, ...prev]);
    setAddModalOpen(false);
  };

  return (
    <>
      <div className="ai-chat-knowledge-hero-status">
        <span className="studio-status-dot"></span>
        <span>{items.length} entries</span>
        {!isAdmin && (
          <>
            <span className="studio-status-sep">|</span>
            <span>View-only — ask an admin to edit or delete an entry</span>
          </>
        )}
      </div>

      <SpaceBetween size="l">
            {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}

            <Container
              header={
                <Header
                  variant="h2"
                  description="Approved facts are injected into every future chat response. Pending/rejected are shown for visibility only."
                  counter={`(${filteredItems.length}${filteredItems.length !== items.length ? ` of ${items.length}` : ""})`}
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Select
                        selectedOption={statusFilter}
                        onChange={({ detail }) => setStatusFilter(detail.selectedOption)}
                        options={STATUS_FILTER_OPTIONS}
                      />
                      <Button iconName="refresh" onClick={() => load(statusFilter.value)} loading={loading}>
                        Refresh
                      </Button>
                      <Button variant="primary" iconName="add-plus" onClick={() => setAddModalOpen(true)}>
                        Add knowledge
                      </Button>
                    </SpaceBetween>
                  }
                >
                  🧠 Learned facts
                </Header>
              }
            >
              {loading ? (
                <Box textAlign="center" padding="l"><Spinner size="large" /></Box>
              ) : (
                <SpaceBetween size="m">
                  <TextFilter
                    filteringText={searchText}
                    onChange={({ detail }) => setSearchText(detail.filteringText)}
                    filteringPlaceholder="Search by content, category, tags, or user..."
                    countText={searchText ? `${filteredItems.length} of ${items.length}` : undefined}
                  />
                  <Table
                    variant="embedded"
                    resizableColumns
                    items={filteredItems}
                  columnDefinitions={[
                    {
                      id: "status", header: "Status", width: 130, minWidth: 110,
                      cell: (item) => (
                        <div className="ai-chat-status-cell">
                          <StatusIndicator type={STATUS_INDICATOR_TYPE[item.status] || "pending"}>
                            {item.status}
                          </StatusIndicator>
                          {isAdmin && (
                            <span className="ai-chat-status-actions">
                              {item.status === "pending" && (
                                <>
                                  <Button
                                    variant="icon"
                                    iconName="check"
                                    ariaLabel="Approve"
                                    loading={decidingId === item.knowledge_id}
                                    onClick={() => handleApprove(item.knowledge_id)}
                                  />
                                  <Button
                                    variant="icon"
                                    iconName="close"
                                    ariaLabel="Reject"
                                    loading={decidingId === item.knowledge_id}
                                    onClick={() => handleReject(item.knowledge_id)}
                                  />
                                </>
                              )}
                              <Button
                                variant="icon"
                                iconName="edit"
                                ariaLabel="Edit"
                                onClick={() => setEditingItem(item)}
                              />
                              <Button
                                variant="icon"
                                iconName="remove"
                                ariaLabel="Delete"
                                loading={deletingId === item.knowledge_id}
                                onClick={() => handleDelete(item.knowledge_id)}
                              />
                            </span>
                          )}
                        </div>
                      ),
                    },
                    { id: "category", header: "Category", width: 140, minWidth: 100, cell: (item) => item.category },
                    {
                      id: "source", header: "Source", width: 130, minWidth: 110,
                      cell: (item) => {
                        const { icon, label, username } = formatSource(item);
                        return (
                          <span className="ai-chat-source-cell">
                            {icon} {label}{username ? ` (${username})` : ""}
                          </span>
                        );
                      },
                    },
                    {
                      id: "content", header: "Content", minWidth: 250,
                      cell: (item) => <span className="ai-chat-content-cell">{item.content}</span>,
                    },
                    {
                      id: "applies_to", header: "Tags", width: 180, minWidth: 120,
                      cell: (item) => (item.applies_to || []).length
                        ? <span className="ai-chat-chip-list">{(item.applies_to || []).map((t, i) => <span key={i} className="ai-chat-chip">{t}</span>)}</span>
                        : "—",
                    },
                    {
                      id: "proposed_at", header: "Proposed", width: 130, minWidth: 85,
                      cell: (item) => item.proposed_at ? new Date(item.proposed_at).toLocaleDateString() : "-",
                    },
                    {
                      id: "decided_by", header: "Approved by", width: 130, minWidth: 85,
                      cell: (item) => item.decided_by || "-",
                    },
                  ]}
                  empty={
                    <Box textAlign="center" color="inherit" padding="l">
                      <b>No learned facts yet</b>
                      <Box color="text-body-secondary">
                        They'll show up here once the agent proposes one from a real conversation
                        and a user approves it — or add one directly with "Add knowledge" above.
                      </Box>
                    </Box>
                  }
                />
                </SpaceBetween>
              )}
            </Container>
      </SpaceBetween>

      {editingItem && (
        <EditKnowledgeModal
          item={editingItem}
          onDismiss={() => setEditingItem(null)}
          onSave={handleSaveEdit}
        />
      )}

      {addModalOpen && (
        <AddKnowledgeModal
          isAdmin={isAdmin}
          onDismiss={() => setAddModalOpen(false)}
          onAdd={handleAdd}
        />
      )}
    </>
  );
}

// ── Top-level page shell: hero + 3-tab switcher ──────────────────────────
export default function KnowledgeAdmin() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("lessons");

  return (
    <div className="image-studio-page ai-chat-knowledge-admin-page">
      <div className="studio-container">
        <ThemeToggle />
        <Link to="/ai-chat" className="studio-back-link" aria-label="Back to AI Chat">
          ← Back to AI Chat
        </Link>

        <div className="studio-hero">
          <div className="studio-hero-left">
            <div className="studio-hero-icon">🧠</div>
            <div className="studio-hero-text">
              <h1 className="studio-hero-title">Knowledge Base Admin</h1>
              <p className="studio-hero-subtitle">
                Everything the AI Chat agent can look up on demand — learned facts,
                table schemas, and reference queries
              </p>
            </div>
          </div>
          <div className="studio-hero-right">
            <Link to="/ai-chat/agent-audit-report" className="studio-back-link" style={{ fontSize: 14 }}>
              📊 Agent Audit Report →
            </Link>
          </div>
        </div>

        <div className="studio-content">
          <Tabs
            activeTabId={activeTab}
            onChange={({ detail }) => setActiveTab(detail.activeTabId)}
            tabs={[
              { id: "lessons", label: "🧠 Lessons", content: <LessonsTab /> },
              { id: "schema", label: "📊 Table Schema", content: <SchemaAdminTab isAdmin={isAdmin} /> },
              { id: "queries", label: "📝 Sample Queries", content: <QueriesAdminTab isAdmin={isAdmin} /> },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

function EditKnowledgeModal({ item, onDismiss, onSave }) {
  const [content, setContent] = useState(item.content);
  const [category, setCategory] = useState(
    CATEGORY_OPTIONS.find((o) => o.value === item.category) || CATEGORY_OPTIONS[2]
  );
  const [appliesTo, setAppliesTo] = useState((item.applies_to || []).join(", "));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const tags = appliesTo.split(",").map((t) => t.trim()).filter(Boolean);
    await onSave({ content, category: category.value, applies_to: tags });
    setSaving(false);
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Edit learned fact"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="Category">
          <Select
            selectedOption={category}
            onChange={({ detail }) => setCategory(detail.selectedOption)}
            options={CATEGORY_OPTIONS}
          />
        </FormField>
        <FormField label="Content">
          <Textarea value={content} onChange={({ detail }) => setContent(detail.value)} rows={6} />
        </FormField>
        <FormField label="Tags (applies to)" description="Comma-separated topics this fact relates to, e.g. gms, fact.orders, channel">
          <Input value={appliesTo} onChange={({ detail }) => setAppliesTo(detail.value)} placeholder="gms, tpv, fact.orders" />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

function AddKnowledgeModal({ isAdmin, onDismiss, onAdd }) {
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(CATEGORY_OPTIONS[2]); // "gotcha" default
  const [appliesTo, setAppliesTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleAdd = async () => {
    if (!content.trim()) {
      setError("Content is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const tags = appliesTo.split(",").map((t) => t.trim()).filter(Boolean);
      await onAdd({ content: content.trim(), category: category.value, applies_to: tags });
    } catch (err) {
      setError(err.message || "Failed to add.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Add knowledge"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleAdd}>
              {isAdmin ? "Add (live immediately)" : "Submit for approval"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error}</Alert>}
        {!isAdmin && (
          <Alert type="info">
            Your addition will be saved as "pending" and won't be used by the agent
            until an admin approves it.
          </Alert>
        )}
        <FormField label="Category">
          <Select
            selectedOption={category}
            onChange={({ detail }) => setCategory(detail.selectedOption)}
            options={CATEGORY_OPTIONS}
          />
        </FormField>
        <FormField
          label="Content"
          description="Write the fact the way you'd want the agent to apply it — e.g. 'GMS excludes refunds. Always filter orderstatus != REFUNDED.'"
        >
          <Textarea
            value={content}
            onChange={({ detail }) => setContent(detail.value)}
            rows={5}
            placeholder="What should the agent remember?"
          />
        </FormField>
        <FormField label="Tags (applies to)" description="Comma-separated topics this fact relates to, e.g. gms, fact.orders, rto">
          <Input value={appliesTo} onChange={({ detail }) => setAppliesTo(detail.value)} placeholder="gms, tpv, fact.orders" />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
